import { TPermission, TRole } from "@/models/roles.model";
import { TUser } from "@/models/user.model";

/**
 * Permission key in the form "module:action", e.g. "callCenter:access".
 * Derived from the permissions catalog returned by GET /permissions/catalog.
 */
export type TPermissionKey = `${string}:${string}`;

/**
 * Modules whose catalog has no "access" permission (e.g. Pipeline Views only has
 * "edit"): their actions don't depend on a module access gate.
 */
export const MODULES_WITHOUT_ACCESS = new Set<string>(["pipelineViews"]);

export const permissionKey =(p: Pick<TPermission, "module" | "action">): TPermissionKey =>
  `${p.module}:${p.action}` as TPermissionKey;

/**
 * Resolve the permission set a user has by aggregating permissions of every role they hold.
 * Unknown role ids (e.g. legacy ids that aren't in the catalog) contribute nothing.
 */
export const getUserPermissionKeys = (
  user: Pick<TUser, "roles"> | null | undefined,
  rolesCatalog: TRole[]
): Set<TPermissionKey> => {
  const keys = new Set<TPermissionKey>();
  if (!user) return keys;
  (user.roles ?? []).forEach((roleId) => {
    const role = rolesCatalog.find((r) => r.id === roleId);
    role?.permissions?.forEach((p) => keys.add(permissionKey(p)));
  });
  return keys;
};

export const userHasPermission = (
  user: Pick<TUser, "roles"> | null | undefined,
  rolesCatalog: TRole[],
  required: TPermissionKey | TPermissionKey[]
): boolean => {
  const keys = getUserPermissionKeys(user, rolesCatalog);
  const list = Array.isArray(required) ? required : [required];
  return list.some((k) => keys.has(k));
};

export const filterUsersByPermission = (
  users: TUser[],
  rolesCatalog: TRole[],
  permission: TPermissionKey | TPermissionKey[]
): TUser[] => users.filter((u) => userHasPermission(u, rolesCatalog, permission));
